<template>
  <div class="claim-screen-container w-full pb-4 sm:pb-8 space-y-4 sm:space-y-6">
    <!-- Top Header & Navigation Segment (New Claim vs History) -->
    <div class="flex items-center justify-between gap-3 pt-1">
      <!-- Vuetify Button Toggle for Views -->
      <v-btn-toggle v-model="activeView" mandatory borderless dense class="bg-slate-100 p-1 rounded-xl shrink-0">
        <v-btn value="form" small depressed :color="activeView === 'form' ? 'white' : 'transparent'"
          class="rounded-lg text-capitalize text-xs font-weight-bold"
          :class="activeView === 'form' ? 'blue--text text--darken-2 shadow-xs' : 'grey--text text--darken-2'">
          {{ isEditingClaim ? 'Edit Claim' : 'New Claim' }}
        </v-btn>
        <v-btn value="history" small depressed :color="activeView === 'history' ? 'white' : 'transparent'"
          class="rounded-lg text-capitalize text-xs font-weight-bold flex items-center gap-1"
          :class="activeView === 'history' ? 'blue--text text--darken-2 shadow-xs' : 'grey--text text--darken-2'">
          <span>History</span>
          <v-chip v-if="userClaims.length" x-small color="blue lighten-5" text-color="blue darken-3"
            class="ml-1 px-1.5 font-weight-bold">
            {{ userClaims.length }}
          </v-chip>
        </v-btn>
      </v-btn-toggle>
    </div>

    <!-- ==================== VIEW 1: NEW CLAIM FORM (MATCHES SCREENSHOT) ==================== -->
    <div v-if="activeView === 'form'" class="space-y-4">
      <!-- Edit Mode Active Alert Banner -->
      <div v-if="isEditingClaim"
        class="bg-amber-50 border border-amber-200/90 rounded-2xl p-3 sm:p-4 flex items-center justify-between gap-3 shadow-2xs">
        <div class="flex items-center gap-2.5 min-w-0">
          <div class="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
            <v-icon color="amber darken-3" size="20">mdi-pencil-outline</v-icon>
          </div>
          <div class="min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <span class="text-xs sm:text-sm font-extrabold text-amber-950 block truncate">
                Editing Claim: {{ editingClaim && editingClaim.billNumber ? '#' + editingClaim.billNumber : 'Expense Claim' }}
              </span>
              <span v-if="editingClaim" class="text-[10px] font-bold px-2 py-0.2 rounded-full border"
                :class="getStatusBadgeClass(getClaimStatus(editingClaim))">
                {{ getClaimStatus(editingClaim) }}
              </span>
            </div>
            <span class="text-[11px] text-amber-800 font-medium block truncate">
              Update details or attachment, then submit to update.
            </span>
          </div>
        </div>
        <v-btn x-small depressed color="white"
          class="amber--text text--darken-3 font-weight-bold rounded-lg border border-amber-300 text-capitalize shrink-0"
          @click="cancelEditClaim">
          Cancel
        </v-btn>
      </div>

      <!-- 1. Minimal Mode Carousel from Claim Settings (Smooth Native Touch & Drag Scrolling) -->
      <div class="mode-carousel-wrapper w-full">
        <div ref="sliderTrack"
          class="mode-carousel-track flex items-center gap-2 overflow-x-auto py-1 px-0.5 no-scrollbar select-none"
          @mousedown="handleSliderMouseDown" @mouseleave="handleSliderMouseLeave" @mouseup="handleSliderMouseUp"
          @mousemove="handleSliderMouseMove">
          <button v-for="(item, idx) in availableModes" :key="item.id || idx" :ref="`modeBtn_${idx}`" type="button"
            @click="selectClaimMode(idx)" :class="[
              'mode-carousel-btn flex items-center gap-2 px-3 py-1.5 sm:py-2 rounded-xl border transition-all cursor-pointer select-none shrink-0',
              selectedModeIndex === idx
                ? 'bg-blue-600 text-white border-blue-600 shadow-xs ring-2 ring-blue-500/20'
                : 'bg-white border-slate-200/90 text-slate-700 hover:border-slate-300 hover:bg-slate-50/80'
            ]"
            :style="selectedModeIndex === idx ? { backgroundColor: $vuetify.theme.currentTheme.employeePrimary, borderColor: $vuetify.theme.currentTheme.employeePrimary } : {}">
            <!-- Icon -->
            <v-icon :class="[
              'w-6 h-6 rounded-lg justify-center shrink-0 transition-colors',
              selectedModeIndex === idx ? 'bg-white/20' : 'bg-slate-100'
            ]" size="15" :color="selectedModeIndex === idx ? 'white' : (item.color || '#475569')">
              {{ item.icon }}
            </v-icon>
            <!-- <div :class="[
              'w-6 h-6 rounded-lg flex items-center justify-center shrink-0 transition-colors',
              selectedModeIndex === idx ? 'bg-white/20' : 'bg-slate-100'
            ]">
            </div> -->

            <!-- Texts -->
            <div class="text-left min-w-0 pr-0.5">
              <span class="block text-xs font-bold leading-tight truncate"
                :class="selectedModeIndex === idx ? 'text-white' : 'text-slate-800'">
                {{ item.label }}
              </span>
              <span class="block text-[10px] font-semibold leading-none mt-0.5"
                :class="selectedModeIndex === idx ? 'text-blue-100' : 'text-slate-500'">
                {{ item.rateLabel }}
              </span>
            </div>
          </button>
        </div>
      </div>

      <!-- 2. Metric Summary Card (Total Limit, Claimed, Remaining Balance) -->
      <div class="bg-[#f0f7ff] border border-[#bae0ff] rounded-2xl p-3.5 sm:p-4 shadow-2xs">
        <div class="grid grid-cols-3 divide-x divide-[#cbe4ff]">
          <!-- Total Limit -->
          <div class="flex flex-col items-center justify-center px-1 text-center">
            <div class="text-[#1677ff] mb-1">
              <!-- Stacked Coins Icon (Exact 3 disks) -->
              <svg class="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 3.34 2 5s4.48 3 10 3 10-1.34 10-3-4.48-3-10-3z" />
                <path d="M2 7.5v3c0 1.66 4.48 3 10 3s10-1.34 10-3v-3c-1.8 1.13-5.57 1.8-10 1.8S3.8 8.63 2 7.5z" />
                <path d="M2 13.5v3c0 1.66 4.48 3 10 3s10-1.34 10-3v-3c-1.8 1.13-5.57 1.8-10 1.8S3.8 14.63 2 13.5z" />
              </svg>
            </div>
            <span class="text-[11px] sm:text-xs font-medium text-slate-600 block leading-tight">
              Total Limit
            </span>
            <span class="text-base sm:text-lg font-black text-slate-900 mt-0.5 tracking-tight">
              ₹{{ formatNumber(currentTotalLimit) }}
            </span>
          </div>

          <!-- Claimed -->
          <div class="flex flex-col items-center justify-center px-1 text-center">
            <div class="text-[#1677ff] mb-1">
              <!-- Wallet / Card Icon -->
              <svg class="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path
                  d="M21 18v1a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v1h-9a2 2 0 00-2 2v8a2 2 0 002 2h9zm-9-2h10V8H12v8zm4-2.5a1.5 1.5 0 110-3 1.5 1.5 0 010 3z" />
              </svg>
            </div>
            <span class="text-[11px] sm:text-xs font-medium text-slate-600 block leading-tight">
              Claimed
            </span>
            <span class="text-base sm:text-lg font-black text-slate-900 mt-0.5 tracking-tight">
              ₹{{ formatNumber(currentClaimedAmount) }}
            </span>
          </div>

          <!-- Remaining Balance -->
          <div class="flex flex-col items-center justify-center px-1 text-center">
            <div class="text-[#1677ff] mb-1">
              <!-- Remaining Wallet Icon -->
              <svg class="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path
                  d="M19 5H5a2 2 0 00-2 2v10a2 2 0 002 2h14a2 2 0 002-2V7a2 2 0 00-2-2zm-1 9h-3a2 2 0 01-2-2 2 2 0 012-2h3v4z" />
              </svg>
            </div>
            <span class="text-[11px] sm:text-xs font-medium text-slate-600 block leading-tight">
              Remaining Balance
            </span>
            <span class="text-base sm:text-lg font-black text-slate-900 mt-0.5 tracking-tight">
              ₹{{ formatNumber(currentRemainingBalance) }}
            </span>
          </div>
        </div>
      </div>

      <!-- 3. Claim Submission Form Card with Vuetify Form Fields -->
      <div class="bg-white rounded-2xl border border-blue-100 shadow-2xs p-4 sm:p-6 space-y-4">
        <!-- Selected Mode Header (Minimal & Sleek) -->
        <div v-if="selectedMode" class="bg-slate-50/80 border border-slate-200/80 rounded-xl p-3 sm:px-4 sm:py-3">
          <!-- Top Row: Icon + Name/Rate + Distance Rate Badge -->
          <div class="flex items-start justify-between gap-3">
            <div class="flex items-center gap-3 min-w-0">
              <v-icon class="w-9 h-9 rounded-xl bg-blue-50/80 border border-blue-100" size="18" :color="selectedMode.color || '#2563eb'">{{ selectedMode.icon }}</v-icon>
              <!-- <div
                class="w-9 h-9 rounded-xl bg-blue-50/80 border border-blue-100 flex items-center justify-center shrink-0">
              </div> -->
              <div class="min-w-0">
                <span class="text-xs sm:text-sm font-extrabold text-slate-900 block leading-tight truncate">
                  {{ selectedMode.fullName }}
                </span>
                <span class="text-[11px] text-slate-500 font-semibold block mt-0.5">
                  {{ selectedMode.rateLabel }}
                </span>
              </div>
            </div>

            <!-- Badge -->
            <span class="text-[11px] font-bold px-2.5 py-0.5 rounded-full shrink-0"
              :class="selectedMode.rateType === 'PER_KM' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-blue-50 text-blue-700 border border-blue-200'">
              {{ selectedModeBadgeText }}
            </span>
          </div>

          <!-- Bottom Row: Auto-calculated metrics pill (full width, clean inline layout) -->
          <div v-if="selectedMode.rateType === 'PER_KM'"
            class="mt-2.5 pt-2 border-t border-slate-200/60 flex items-center justify-between text-[11px] text-slate-600">
            <div class="flex items-center gap-1.5 min-w-0 truncate">
              <v-icon x-small color="emerald darken-1">mdi-map-marker-distance</v-icon>
              <span class="text-slate-500 font-medium">GPS Auto-Calculated:</span>
              <strong class="text-slate-900 font-bold">{{ totalMonthlyDistance }} km</strong>
            </div>
            <div
              class="shrink-0 flex items-center gap-1 bg-emerald-50 text-emerald-800 border border-emerald-200/80 px-2 py-0.5 rounded-lg font-extrabold text-xs">
              <span class="text-[10px] text-emerald-600 font-normal">Est.</span>
              ₹{{ Math.round(totalMonthlyDistance * (selectedMode.pricePerKm || 0)) }}
            </div>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <!-- Vuetify Select: Month -->
          <div>
            <label class="block text-xs font-semibold text-slate-800 mb-1.5">
              <span class="text-red-500 font-bold">*</span> Month
            </label>
            <v-select v-model="formState.month" :items="monthOptions" item-text="label" item-value="value" outlined
              dense hide-details="auto" append-icon="mdi-chevron-down" placeholder="Select Month"
              class="custom-vuetify-input"
              :menu-props="{ rounded: 'xl', offsetY: true, contentClass: 'elevation-3' }" />
          </div>

          <!-- Vuetify Menu & Date Picker: Bill Date -->
          <div>
            <label class="block text-xs font-semibold text-slate-800 mb-1.5">
              <span class="text-red-500 font-bold">*</span> Expense Date
            </label>
            <v-menu v-model="dateMenu" :close-on-content-click="false" transition="scale-transition" offset-y
              min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field v-model="formState.billDate" placeholder="Select date"
                  append-icon="mdi-calendar-blank-outline" readonly outlined dense hide-details="auto" v-bind="attrs"
                  v-on="on" class="custom-vuetify-input cursor-pointer" />
              </template>
              <v-date-picker v-model="formState.billDate" :color="$vuetify.theme.currentTheme.employeePrimary"
                :max="todayDate" :min="minAllowedDate" @input="dateMenu = false"></v-date-picker>
            </v-menu>
          </div>

          <!-- Trip Distance (km) - Only if PER_KM mode -->
          <div v-if="selectedMode && selectedMode.rateType === 'PER_KM'">
            <div class="flex items-center justify-between mb-1.5">
              <label class="text-xs font-semibold text-slate-800 mb-0">
                <span class="text-red-500 font-bold">*</span> Distance (km)
              </label>
              <div class="flex items-center gap-1.5">
                <span class="text-[10px] text-emerald-700 font-bold">
                  @ ₹{{ selectedMode.pricePerKm }}/km
                </span>
                <!-- <span class="text-[10px] bg-blue-50 text-blue-700 font-extrabold px-1.5 py-0.5 rounded-md border border-blue-200">
                  Auto-calculated (Editable)
                </span> -->
              </div>
            </div>
            <v-text-field v-model.number="travelDistance" @input="onDistanceInput" type="number" min="0" step="0.1"
              suffix="km" placeholder="0 km" outlined dense hide-details="auto"
              class="custom-vuetify-input font-bold" />
            <div class="flex items-center justify-between mt-1 px-0.5">
              <span class="text-[11px] text-slate-500 font-medium">
                Auto-calculated GPS: <strong class="text-blue-700 font-bold cursor-pointer hover:underline"
                  @click="resetToGpsDistance">{{ totalMonthlyDistance }} km</strong>
              </span>
              <span v-if="dailyDistances[formState.billDate]" class="text-[11px] text-slate-500 font-medium">
                Day Travel: <strong class="text-emerald-700 font-bold cursor-pointer hover:underline"
                  @click="setDayGpsDistance">{{ (dailyDistances[formState.billDate] || 0).toFixed(1) }} km</strong>
              </span>
            </div>
          </div>

          <!-- Vuetify Text Field: Claim Amount -->
          <div>
            <div class="flex items-center justify-between mb-1.5">
              <label class="text-xs font-semibold text-slate-800 mb-0">
                <span class="text-red-500 font-bold">*</span> Claim Amount
              </label>
              <!-- <span v-if="selectedMode && selectedMode.rateType === 'PER_KM'"
                class="text-[10px] text-blue-600 font-bold">
                Auto-calculated
              </span> -->
              <span v-if="selectedMode && selectedMode.rateType === 'FLAT' && selectedModeMaxLimit"
                class="text-[10px] text-amber-700 font-bold">
                Max Allowed: ₹{{ formatNumber(selectedModeMaxLimit) }}
              </span>
            </div>
            <v-text-field v-model.number="formState.amount" type="number" min="1" prefix="₹"
              :placeholder="selectedMode && selectedMode.rateType === 'FLAT' && selectedModeMaxLimit ? `Enter amount (Up to ₹${formatNumber(selectedModeMaxLimit)})` : 'Enter claim amount'"
              outlined dense hide-details="auto" class="custom-vuetify-input font-bold" />
            <span
              v-if="selectedMode && selectedMode.rateType === 'FLAT' && selectedModeMaxLimit && formState.amount > selectedModeMaxLimit"
              class="text-[11px] text-red-600 font-medium mt-1 block">
              {{ maxLimitWarningText }}
            </span>
            <span v-if="formState.amount && formState.amount > currentRemainingBalance"
              class="text-[11px] text-amber-600 font-medium mt-1 block">
              Notice: This amount exceeds your remaining balance (₹{{ formatNumber(currentRemainingBalance) }}).
            </span>
            <span v-if="claimPolicies.maxDailyClaimCap && formState.amount > claimPolicies.maxDailyClaimCap"
              class="text-[11px] text-red-600 font-medium mt-1 block">
              Warning: Exceeds company policy daily cap of ₹{{ formatNumber(claimPolicies.maxDailyClaimCap) }}.
            </span>
          </div>

          <!-- Vuetify Text Field: Bill / Reference Number -->
          <div :class="{ 'md:col-span-2': selectedMode && selectedMode.rateType !== 'PER_KM' }">
            <label class="block text-xs font-semibold text-slate-800 mb-1.5">
              <span v-if="isReceiptMandatory" class="text-red-500 font-bold">*</span>
              {{ billNumberFieldLabel }}
            </label>
            <v-text-field v-model.trim="formState.billNumber" :placeholder="billNumberFieldPlaceholder" outlined dense
              hide-details="auto" class="custom-vuetify-input" />
          </div>

          <!-- Vuetify Textarea: Comment (Optional) -->
          <div class="md:col-span-2">
            <label class="block text-xs font-semibold text-slate-800 mb-1.5">
              Trip Details / Comment
            </label>
            <v-textarea v-model.trim="formState.comment" rows="2" counter="250"
              :placeholder="selectedMode && selectedMode.categoryType === 'travel' ? 'e.g. Field visit to client site, customer meetings' : 'Add any additional information...'"
              outlined dense hide-details="auto" class="custom-vuetify-input" />
          </div>

          <!-- Attachments Field with Dynamic Policy Requirement -->
          <div class="md:col-span-2">
            <div class="flex items-center justify-between mb-1.5">
              <label class="text-xs font-semibold text-slate-800 mb-0">
                <span v-if="isReceiptMandatory" class="text-red-500 font-bold">*</span>
                Attachments / Receipts
              </label>
              <span :class="[
                'text-[10px] font-bold px-2 py-0.5 rounded-full border',
                isReceiptMandatory
                  ? 'bg-red-50 text-red-700 border-red-200'
                  : 'bg-emerald-50 text-emerald-700 border-emerald-200'
              ]">
                {{ receiptPolicyBadgeText }}
              </span>
            </div>

            <!-- Hidden Native File Input -->
            <input type="file" ref="fileInputRef" class="hidden" accept=".pdf,.jpg,.jpeg,.png"
              @change="handleFileChange" />

            <!-- Case 1: Newly Selected File From Device -->
            <div v-if="selectedFile"
              class="border border-blue-200 bg-blue-50/50 rounded-xl p-3 flex items-center justify-between gap-3">
              <div class="flex items-center gap-3 min-w-0">
                <div
                  class="w-10 h-10 rounded-lg bg-white border border-blue-100 flex items-center justify-center shrink-0 overflow-hidden">
                  <img v-if="filePreviewUrl" :src="filePreviewUrl" alt="Receipt Preview"
                    class="w-full h-full object-cover" />
                  <v-icon v-else color="red darken-1">mdi-file-pdf-box</v-icon>
                </div>
                <div class="min-w-0">
                  <p class="text-xs font-bold text-slate-800 truncate mb-0.5">
                    {{ selectedFile.name }}
                  </p>
                  <span class="text-[11px] text-slate-500">
                    {{ formatFileSize(selectedFile.size) }} • New File
                  </span>
                </div>
              </div>
              <v-btn icon small color="red" @click.stop="removeSelectedFile" class="hover:bg-red-50 shrink-0"
                title="Remove Attachment">
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </div>

            <!-- Case 2: Pre-existing Attached Bill From Edited Claim -->
            <div v-else-if="existingAttachedBill"
              class="border border-emerald-200 bg-emerald-50/50 rounded-xl p-3 flex items-center justify-between gap-3">
              <div class="flex items-center gap-3 min-w-0">
                <div
                  class="w-10 h-10 rounded-lg bg-white border border-emerald-200 flex items-center justify-center shrink-0">
                  <v-icon color="emerald darken-1">mdi-file-check-outline</v-icon>
                </div>
                <div class="min-w-0">
                  <p class="text-xs font-bold text-slate-800 truncate mb-0.5">
                    Current Attached Receipt
                  </p>
                  <a :href="getAttachmentFullUrl(existingAttachedBill)" target="_blank" rel="noopener noreferrer"
                    class="text-[11px] text-blue-600 hover:text-blue-800 font-bold inline-flex items-center gap-1">
                    <v-icon x-small color="primary">mdi-open-in-new</v-icon> View Current Receipt
                  </a>
                </div>
              </div>
              <div class="flex items-center gap-1.5 shrink-0">
                <v-btn x-small depressed color="primary" class="rounded-lg text-capitalize font-weight-bold"
                  @click="triggerFileInput">
                  Change File
                </v-btn>
                <v-btn icon small color="red" @click.stop="removeExistingAttachment" class="hover:bg-red-50"
                  title="Remove Existing Receipt">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </div>
            </div>

            <!-- Case 3: No File or Existing Attachment (Dropzone + Optional URL Link) -->
            <div v-else class="space-y-2">
              <div v-if="!showUrlAttachmentInput" @click="triggerFileInput"
                class="border-2 border-dashed border-[#bae0ff] hover:border-[#1677ff] rounded-2xl p-4 sm:p-5 flex flex-col items-center justify-center text-center cursor-pointer hover:bg-blue-50/40 transition-colors bg-white select-none">
                <div class="w-10 h-10 rounded-full bg-blue-50 text-[#1677ff] flex items-center justify-center mb-1.5">
                  <v-icon color="#1677ff" size="28">mdi-cloud-upload-outline</v-icon>
                </div>
                <span class="text-xs sm:text-sm font-bold text-[#1677ff]">
                  {{ uploadReceiptText }}
                </span>
                <span class="text-[11px] text-slate-400 mt-0.5 font-medium">
                  PDF, JPG, PNG (Max 5MB)
                </span>
              </div>

              <!-- URL Link Input Alternative -->
              <div v-if="showUrlAttachmentInput" class="p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
                <div class="flex items-center justify-between">
                  <span class="text-xs font-bold text-slate-700 flex items-center gap-1">
                    <v-icon small color="primary">mdi-link-variant</v-icon> Link Attachment URL
                  </span>
                  <button type="button" @click="showUrlAttachmentInput = false; attachmentUrlInput = ''"
                    class="text-xs text-slate-500 hover:text-slate-800 font-semibold cursor-pointer">
                    Upload File Instead
                  </button>
                </div>
                <v-text-field v-model.trim="attachmentUrlInput" placeholder="https://example.com/receipt.pdf" outlined
                  dense hide-details="auto" class="custom-vuetify-input" prepend-inner-icon="mdi-link-variant" />
              </div>

              <!-- Toggle Button to Switch to Link URL -->
              <div v-if="!showUrlAttachmentInput" class="flex justify-end">
                <button type="button" @click="showUrlAttachmentInput = true"
                  class="text-[11px] font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1 cursor-pointer">
                  <v-icon x-small color="primary">mdi-link-variant</v-icon>
                  <span>Or link document via URL</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 4. Vuetify Submit / Update Claim Action Buttons -->
      <div v-if="isEditingClaim" class="flex items-center gap-2.5">
        <v-btn depressed small outlined color="grey darken-1" @click="cancelEditClaim"
          class="rounded-xl font-weight-bold text-capitalize text-xs sm:text-sm h-12 px-4 shrink-0">
          Cancel
        </v-btn>
        <v-btn depressed large :color="$vuetify.theme.currentTheme.employeePrimary" :loading="isSubmitting"
          :disabled="isSubmitting" @click="handleSubmitClaim"
          class="flex-1 rounded-xl white--text font-weight-bold text-capitalize text-sm sm:text-base h-12 shadow-xs">
          Update Claim
        </v-btn>
      </div>
      <v-btn v-else block depressed large :color="$vuetify.theme.currentTheme.employeePrimary" :loading="isSubmitting"
        :disabled="isSubmitting" @click="handleSubmitClaim"
        class="rounded-xl white--text font-weight-bold text-capitalize text-sm sm:text-base h-12 shadow-xs">
        Submit Claim
      </v-btn>
    </div>

    <!-- ==================== VIEW 2: CLAIM HISTORY ==================== -->
    <div v-else class="space-y-4">
      <!-- Vuetify Status Filter Chips -->
      <div class="flex items-center gap-1.5 overflow-x-auto pb-1 no-scrollbar">
        <v-chip v-for="st in statusFilters" :key="st.value" filter small :outlined="selectedStatusFilter !== st.value"
          :color="selectedStatusFilter === st.value ? $vuetify.theme.currentTheme.employeePrimary : 'grey lighten-2'"
          :text-color="selectedStatusFilter === st.value ? 'white' : 'grey darken-3'"
          @click="selectedStatusFilter = st.value" class="font-weight-medium text-xs cursor-pointer">
          {{ st.label }}
        </v-chip>
      </div>

      <!-- Loading State -->
      <div v-if="isLoadingClaims" class="py-12 flex flex-col items-center justify-center">
        <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
        <span class="text-xs text-slate-500 font-medium mt-2">Loading claim records...</span>
      </div>

      <!-- Empty State -->
      <div v-else-if="!filteredClaims.length" class="bg-white rounded-2xl border border-slate-200 p-8 text-center">
        <div class="w-14 h-14 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mx-auto mb-3">
          <v-icon color="#1677ff" size="30">mdi-clipboard-text-clock-outline</v-icon>
        </div>
        <h3 class="text-sm font-bold text-slate-800 mb-1">No Claims Found</h3>
        <p class="text-xs text-slate-500 mb-4 max-w-xs mx-auto">
          {{ emptyStateMessage }}
        </p>
        <v-btn depressed small :color="$vuetify.theme.currentTheme.employeePrimary"
          class="white--text rounded-xl font-weight-bold text-capitalize" @click="activeView = 'form'">
          Submit Claim
        </v-btn>
      </div>

      <!-- Claims List -->
      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3.5">
        <div v-for="item in filteredClaims" :key="item._id || item.id"
          class="bg-white rounded-2xl border border-slate-200 p-4 shadow-2xs hover:border-blue-200 transition-all flex flex-col justify-between space-y-3">
          <!-- Top Row: Category, Bill # and Status Badge -->
          <div class="flex items-start justify-between gap-2">
            <div>
              <div class="flex items-center gap-2">
                <span class="text-xs font-bold text-slate-900">
                  {{ getCategoryLabel(item.reimbursementType) }}
                </span>
                <span v-if="item.billNumber"
                  class="text-[11px] font-mono text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                  #{{ item.billNumber }}
                </span>
              </div>
              <span class="text-[11px] text-slate-400 block mt-0.5">
                {{ formatDateTime(item.createdAt) }}
              </span>
            </div>

            <!-- Status Pill -->
            <span class="px-2.5 py-1 rounded-full text-[10px] font-extrabold tracking-wide uppercase border"
              :class="getStatusBadgeClass(getClaimStatus(item))">
              {{ getClaimStatus(item) }}
            </span>
          </div>

          <!-- Amount & Month Grid -->
          <div class="grid grid-cols-2 gap-2 bg-slate-50 rounded-xl p-2.5 text-xs">
            <div>
              <span class="text-slate-400 text-[10px] block">Claimed Amount</span>
              <span class="font-extrabold text-slate-900 text-sm">
                ₹{{ formatNumber(item.claimAmount) }}
              </span>
            </div>
            <div>
              <span class="text-slate-400 text-[10px] block">For Month / Date</span>
              <span class="font-semibold text-slate-700">
                {{ item.month || 'N/A' }} {{ item.year ? `(${item.year})` : '' }}
              </span>
            </div>
          </div>

          <!-- Bill Date & Comments if any -->
          <div v-if="item.comments && item.comments.length" @click="openDiscussionModal(item)"
            class="text-xs text-slate-600 bg-blue-50/50 hover:bg-blue-100/50 cursor-pointer rounded-lg p-2 border border-blue-100 transition-colors">
            <div class="flex items-center justify-between gap-1 mb-0.5">
              <span class="font-bold text-slate-700 text-[11px] flex items-center gap-1">
                <v-icon x-small color="primary">mdi-chat-processing-outline</v-icon>
                <span>Latest Note ({{ item.comments.length }} message{{ item.comments.length > 1 ? 's' : '' }}):</span>
              </span>
              <span class="text-[10px] text-blue-600 font-semibold">View thread →</span>
            </div>
            <p class="mb-0 text-slate-600 line-clamp-2">
              {{ item.comments[item.comments.length - 1].message }}
            </p>
          </div>

          <!-- Actions Row: View Attachment, Discussion & Edit -->
          <div class="flex items-center justify-between pt-1 border-t border-slate-100 text-xs">
            <div class="flex items-center gap-2">
              <a v-if="item.attachedBill" :href="getAttachmentFullUrl(item.attachedBill)" target="_blank"
                rel="noopener noreferrer"
                class="text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1 no-underline">
                <v-icon small color="primary">mdi-link-variant</v-icon>
                <span>Receipt</span>
              </a>
              <span v-else class="text-slate-400 text-[11px]">No receipt</span>

              <span class="text-slate-300">•</span>

              <button type="button" @click="openDiscussionModal(item)"
                class="text-slate-700 hover:text-blue-600 font-semibold flex items-center gap-1 cursor-pointer">
                <v-icon x-small color="grey darken-1">mdi-forum-outline</v-icon>
                <span>Discussion</span>
                <span v-if="item.comments && item.comments.length"
                  class="text-[10px] bg-slate-100 px-1.5 py-0.2 rounded-full font-bold">
                  {{ item.comments.length }}
                </span>
              </button>
            </div>

            <!-- Edit button only if Pending -->
            <v-btn v-if="canEditClaim(item)" text x-small :color="$vuetify.theme.currentTheme.employeePrimary"
              class="font-weight-bold rounded-lg text-capitalize" @click="openEditClaimInForm(item)">
              Edit Claim
            </v-btn>
          </div>
        </div>
      </div>
    </div>

    <!-- Timeline & Two-Way Discussion Modal Dialog -->
    <v-dialog v-model="discussionModalOpen" max-width="500" scrollable>
      <v-card v-if="selectedDiscussionClaim" class="rounded-3xl overflow-hidden flex flex-col max-h-[85vh]">
        <!-- Dialog Header -->
        <div class="px-5 py-3.5 bg-slate-50 border-b border-slate-100 flex items-center justify-between shrink-0">
          <div>
            <h3 class="text-sm font-extrabold text-slate-900 mb-0">
              Claim Discussion & Timeline
            </h3>
            <span class="text-[11px] text-slate-400">
              {{ selectedDiscussionClaim.reimbursementTypeName ||
                getCategoryLabel(selectedDiscussionClaim.reimbursementType) }}
              <span v-if="selectedDiscussionClaim.billNumber" class="font-mono text-slate-500">#{{
                selectedDiscussionClaim.billNumber }}</span>
            </span>
          </div>
          <v-btn icon small @click="closeDiscussionModal">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- Summary Banner -->
        <div class="px-5 py-3 bg-blue-50/60 border-b border-blue-100/60 flex items-center justify-between shrink-0">
          <div>
            <span class="text-[10px] font-bold uppercase tracking-wider text-slate-500 block">Claim Amount</span>
            <span class="text-base font-black text-slate-900">₹{{ formatNumber(selectedDiscussionClaim.claimAmount)
            }}</span>
            <div v-if="selectedDiscussionClaim.travelDetails && selectedDiscussionClaim.travelDetails.mode"
              class="text-[11px] text-blue-700 font-medium mt-0.5 flex items-center gap-1">
              <v-icon x-small color="blue">mdi-car</v-icon>
              <span>{{ selectedDiscussionClaim.travelDetails.mode }}</span>
              <span v-if="selectedDiscussionClaim.travelDetails.distanceKm">• {{
                selectedDiscussionClaim.travelDetails.distanceKm }} km</span>
            </div>
          </div>
          <span class="px-2.5 py-1 rounded-full text-[10px] font-extrabold tracking-wide uppercase border"
            :class="getStatusBadgeClass(getClaimStatus(selectedDiscussionClaim))">
            {{ getClaimStatus(selectedDiscussionClaim) }}
          </span>
        </div>

        <!-- Scrollable Conversation & Timeline Body -->
        <v-card-text class="px-4 py-3 overflow-y-auto flex-grow space-y-4">
          <!-- Approval History Timeline -->
          <div v-if="selectedDiscussionClaim.approvals && selectedDiscussionClaim.approvals.length"
            class="bg-slate-50 rounded-2xl p-3 border border-slate-200/70">
            <span class="text-[10px] font-extrabold text-slate-500 uppercase tracking-wider block mb-2">Status
              Timeline</span>
            <div class="space-y-2">
              <div v-for="(appr, aIdx) in selectedDiscussionClaim.approvals" :key="aIdx"
                class="flex items-center justify-between text-xs pb-1.5 border-b border-slate-200/40 last:border-0 last:pb-0">
                <div class="flex items-center gap-2">
                  <span class="w-2 h-2 rounded-full" :class="getTimelineDotClass(appr.status)"></span>
                  <span class="font-bold text-slate-800">{{ appr.status }}</span>
                  <span v-if="appr.updatedByName" class="text-slate-500 text-[11px]">({{ appr.updatedByName }})</span>
                </div>
                <span class="text-[10px] text-slate-400">{{ formatFullDateTime(appr.updatedAt) }}</span>
              </div>
            </div>
          </div>

          <!-- Messages Stream -->
          <div class="space-y-2.5">
            <span class="text-[10px] font-extrabold text-slate-500 uppercase tracking-wider block mb-1">
              Discussion Messages
            </span>

            <div v-if="!selectedDiscussionClaim.comments || !selectedDiscussionClaim.comments.length"
              class="text-center py-6 text-slate-400 text-xs bg-slate-50 rounded-2xl border border-dashed border-slate-200">
              <v-icon color="grey lighten-1" class="mb-1">mdi-message-outline</v-icon>
              <div>No messages yet. Send a note or ask a question below.</div>
            </div>

            <div v-else class="space-y-2.5">
              <div v-for="(c, cIdx) in selectedDiscussionClaim.comments" :key="cIdx" :class="[
                'p-3 rounded-2xl max-w-[85%] text-xs shadow-2xs',
                isCommentByEmployee(c)
                  ? 'ml-auto bg-blue-600 text-white rounded-br-xs'
                  : 'mr-auto bg-slate-100 text-slate-800 rounded-bl-xs border border-slate-200/70'
              ]">
                <div class="flex items-center justify-between gap-2 text-[10px] mb-1 opacity-80">
                  <span class="font-bold">
                    {{ isCommentByEmployee(c) ? 'You' : (c.commentedByName || 'Manager / Admin') }}
                    <span class="text-[9px] px-1 py-0.2 rounded font-semibold uppercase ml-1"
                      :class="isCommentByEmployee(c) ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-600'">
                      {{ c.commentedByRole || (isCommentByEmployee(c) ? 'EMPLOYEE' : 'ADMIN') }}
                    </span>
                  </span>
                  <span>{{ formatFullDateTime(c.commentedAt) }}</span>
                </div>
                <p class="mb-0 text-xs leading-relaxed whitespace-pre-wrap">{{ c.message }}</p>
              </div>
            </div>
          </div>
        </v-card-text>

        <!-- Message Input Bar at Bottom -->
        <div class="p-3 border-t border-slate-100 bg-white flex items-center gap-2 shrink-0">
          <v-text-field v-model="discussionReplyText" placeholder="Type a message, reply, or query..." outlined dense
            hide-details class="text-xs" @keydown.enter="sendDiscussionReply" />
          <v-btn small fab depressed :color="$vuetify.theme.currentTheme.employeePrimary"
            :loading="isSendingDiscussionReply" :disabled="!discussionReplyText || !discussionReplyText.trim()"
            @click="sendDiscussionReply">
            <v-icon small color="white">mdi-send</v-icon>
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import {
  getClaimSettings,
  getUserReimbursements,
  uploadReimbursementDocument,
  submitReimbursement,
  updateReimbursement,
  addClaimComment
} from '@/app/services/claim'
import { getLocationOverview } from '@/app/services/dashboard'

export default {
  name: 'AppClaim',
  data() {
    return {
      activeView: 'form', // 'form' | 'history'
      isLoadingConfig: false,
      isLoadingClaims: false,
      isSubmitting: false,
      isUpdating: false,
      dateMenu: false,
      editDateMenu: false,
      discussionModalOpen: false,
      selectedDiscussionClaim: null,
      discussionReplyText: '',
      isSendingDiscussionReply: false,

      isDraggingSlider: false,
      sliderStartX: 0,
      sliderScrollLeft: 0,

      // Selected Mode in Minimal Carousel
      selectedModeIndex: 0,
      travelDistance: 0,
      dailyDistances: {},
      totalMonthlyDistance: 0,

      // In-Form Edit Claim State
      isEditingClaim: false,
      editingClaim: null,
      existingAttachedBill: null,
      attachmentUrlInput: '',
      showUrlAttachmentInput: false,

      // Travel Modes from Admin Settings
      travelModes: [
        { mode: 'TWO_WHEELER', rateType: 'PER_KM', pricePerKm: 5, min: 0, max: 0, requireReceipt: false, status: 'ACTIVE' },
        { mode: 'FOUR_WHEELER', rateType: 'PER_KM', pricePerKm: 10, min: 0, max: 0, requireReceipt: false, status: 'ACTIVE' },
        { mode: 'AUTO', rateType: 'PER_KM', pricePerKm: 8, min: 0, max: 0, requireReceipt: false, status: 'ACTIVE' },
        { mode: 'BUS', rateType: 'FLAT', flatAmount: 150, min: 0, max: 150, requireReceipt: false, status: 'ACTIVE' },
        { mode: 'TRAIN', rateType: 'FLAT', flatAmount: 500, min: 0, max: 500, requireReceipt: true, status: 'ACTIVE' },
        { mode: 'FLIGHT', rateType: 'FLAT', flatAmount: 3500, min: 0, max: 3500, requireReceipt: true, status: 'ACTIVE' }
      ],

      // Allowances from Admin Settings
      allowances: [
        { type: 'DAILY_DA', amount: 300, requireReceipt: false, status: 'ACTIVE' },
        { type: 'HALF_DAY_DA', amount: 150, requireReceipt: false, status: 'ACTIVE' },
        { type: 'OUTSTATION_DA', amount: 600, requireReceipt: false, status: 'ACTIVE' },
        { type: 'NIGHT_STAY_DA', amount: 1200, requireReceipt: true, status: 'ACTIVE' },
        { type: 'MEAL_DA', amount: 200, requireReceipt: false, status: 'ACTIVE' }
      ],

      // Claim Policies from Admin
      claimPolicies: {
        maxDailyClaimCap: 5000,
        receiptThreshold: 500,
        allowPastDateClaims: true,
        submissionWindowDays: 30
      },

      // Form State
      formState: {
        month: 8, // 0-indexed: 8 is September
        billNumber: '',
        billDate: dayjs().format('YYYY-MM-DD'),
        amount: 0,
        comment: ''
      },

      // File Upload State
      selectedFile: null,
      filePreviewUrl: null,

      // Claims Data
      userClaims: [],
      selectedStatusFilter: 'ALL',
      statusFilters: [
        { label: 'All', value: 'ALL' },
        { label: 'Pending', value: 'PENDING' },
        { label: 'Approved', value: 'APPROVED' },
        { label: 'Rejected', value: 'REJECTED' }
      ]
    }
  },
  computed: {
    availableModes() {
      const list = []

      // 1. Travel Modes from Settings
      if (this.travelModes && this.travelModes.length > 0) {
        this.travelModes.forEach((tm) => {
          if (tm.status === 'INACTIVE' || tm.isActive === false) return
          const meta = this.getModeMeta(tm.mode)
          const isPerKm = tm.rateType === 'PER_KM' || (!tm.rateType && Number(tm.pricePerKm) > 0)
          const maxLimit = isPerKm
            ? (Number(tm.max) || 0)
            : Math.max(Number(tm.flatAmount || 0), Number(tm.max || 0), Number(tm.min || 0))
          const rateLabel = isPerKm
            ? `₹${tm.pricePerKm || 0}/km`
            : `Max ₹${maxLimit}`

          list.push({
            id: `travel_${tm.mode}`,
            key: tm.mode,
            mode: tm.mode,
            label: this.getModeShortLabel(tm.mode),
            fullName: this.formatModeName(tm.mode),
            categoryType: 'travel',
            rateType: isPerKm ? 'PER_KM' : 'FLAT',
            pricePerKm: Number(tm.pricePerKm) || 0,
            flatAmount: maxLimit,
            min: Number(tm.min) || 0,
            max: maxLimit,
            requireReceipt: !!tm.requireReceipt || !!tm.requiresReceipt,
            icon: meta.icon,
            color: meta.color,
            rateLabel
          })
        })
      }

      // 2. Allowances from Settings
      if (this.allowances && this.allowances.length > 0) {
        this.allowances.forEach((al) => {
          if (al.status === 'INACTIVE' || al.isActive === false) return
          list.push({
            id: `allowance_${al.type}`,
            key: al.type,
            mode: al.type,
            label: this.getAllowanceShortLabel(al.type),
            fullName: this.formatAllowanceTypeName(al.type),
            categoryType: 'allowance',
            rateType: 'FLAT',
            flatAmount: Number(al.amount) || 0,
            requireReceipt: !!al.requireReceipt || !!al.requiresReceipt,
            icon: this.getAllowanceIcon(al.type),
            color: '#9333ea',
            rateLabel: `₹${al.amount || 0}`
          })
        })
      }

      return list
    },
    selectedMode() {
      if (!this.availableModes || !this.availableModes.length) return null
      return this.availableModes[this.selectedModeIndex] || this.availableModes[0]
    },
    selectedModeMaxLimit() {
      if (!this.selectedMode) return 0
      return Number(this.selectedMode.maxLimit || this.selectedMode.max || this.selectedMode.flatAmount || 0)
    },
    isReceiptMandatory() {
      if (this.selectedMode?.requireReceipt) {
        return true
      }
      const threshold = Number(this.claimPolicies?.receiptThreshold) || 500
      const amount = Number(this.formState.amount) || 0
      return amount > threshold
    },
    todayDate() {
      return dayjs().format('YYYY-MM-DD')
    },
    minAllowedDate() {
      if (this.claimPolicies?.allowPastDateClaims === false) {
        return this.todayDate
      }
      const windowDays = Number(this.claimPolicies?.submissionWindowDays) || 30
      return dayjs().subtract(windowDays, 'day').format('YYYY-MM-DD')
    },
    currentUser() {
      return this.$storage?.getUniversal('user') || JSON.parse(localStorage.getItem('user')) || {}
    },
    currentUserId() {
      return this.currentUser?._id || this.currentUser?.user?._id || ''
    },
    userBranchId() {
      return (
        this.currentUser?.branch?._id ||
        this.currentUser?.branch ||
        this.currentUser?.user?.branch?._id ||
        this.currentUser?.user?.branch ||
        ''
      )
    },
    s3BaseUrl() {
      return process.env.VUE_APP_S3_BASE_URL || 'https://uat-cdn.myfinfi.com/'
    },
    monthOptions() {
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ]
      const currentYear = dayjs().year()
      const currentMonth = dayjs().month() // 0-11
      const startYear = currentMonth >= 3 ? currentYear : currentYear - 1

      // Standard Indian Financial Year order: Apr (3) to Mar (2)
      const fyOrder = [3, 4, 5, 6, 7, 8, 9, 10, 11, 0, 1, 2]
      return fyOrder.map((monthIdx) => {
        const year = monthIdx >= 3 ? startYear : startYear + 1
        return {
          label: `${monthNames[monthIdx]} ${year}`,
          monthName: monthNames[monthIdx],
          year,
          value: monthIdx
        }
      })
    },
    currentTotalLimit() {
      if (this.selectedMode?.max && Number(this.selectedMode.max) > 0) {
        return Number(this.selectedMode.max)
      }
      if (this.selectedMode?.flatAmount && Number(this.selectedMode.flatAmount) > 0) {
        return Number(this.selectedMode.flatAmount)
      }
      return Number(this.claimPolicies?.maxDailyClaimCap) || 5000
    },
    currentClaimedAmount() {
      if (!this.userClaims || !this.userClaims.length || !this.selectedMode) return 0
      const selectedMonthObj = this.monthOptions.find((m) => m.value === this.formState.month)
      const selectedMonthName = selectedMonthObj?.monthName?.toLowerCase()

      return this.userClaims.reduce((sum, item) => {
        const monthMatch =
          !selectedMonthName ||
          (item.month && item.month.toLowerCase().includes(selectedMonthName))

        if (!monthMatch) return sum

        const targetCatId = this.getTargetCategoryId(this.selectedMode)
        const typeMatch =
          item.reimbursementType === targetCatId ||
          item.reimbursementType === this.selectedMode.id ||
          item.reimbursementType === this.selectedMode.key ||
          (item.reimbursementTypeName &&
            (item.reimbursementTypeName.toLowerCase().includes(this.selectedMode.label.toLowerCase()) ||
              (this.selectedMode.categoryType === 'travel' && item.reimbursementTypeName.toLowerCase().includes('travel')) ||
              (this.selectedMode.categoryType === 'allowance' && (item.reimbursementTypeName.toLowerCase().includes('allowance') || item.reimbursementTypeName.toLowerCase().includes('da')))))

        if (typeMatch) {
          return sum + (Number(item.claimAmount) || 0)
        }
        return sum
      }, 0)
    },
    currentRemainingBalance() {
      return Math.max(0, this.currentTotalLimit - this.currentClaimedAmount)
    },
    filteredClaims() {
      if (!this.userClaims) return []
      if (this.selectedStatusFilter === 'ALL') return this.userClaims
      return this.userClaims.filter((c) => {
        const status = this.getClaimStatus(c).toUpperCase()
        return status === this.selectedStatusFilter
      })
    },
    emptyStateMessage() {
      if (this.selectedStatusFilter !== 'ALL') {
        return `No claims with status "${this.selectedStatusFilter}".`
      }
      return 'You have not submitted any corporate expense claims yet.'
    },
    selectedModeBadgeText() {
      if (this.selectedMode?.rateType === 'PER_KM') return 'Distance Rate'
      if (this.selectedMode?.categoryType === 'allowance') return 'Daily Allowance'
      return 'Fixed Max Limit'
    },
    maxLimitWarningText() {
      if (!this.selectedMode) return ''
      return `Warning: Exceeds maximum limit of ₹${this.formatNumber(this.selectedModeMaxLimit)} for ${this.selectedMode.fullName}.`
    },
    billNumberFieldLabel() {
      return this.selectedMode && this.selectedMode.categoryType === 'travel'
        ? 'Ticket / Reference Number'
        : 'Bill / Receipt Number'
    },
    billNumberFieldPlaceholder() {
      return this.selectedMode && this.selectedMode.categoryType === 'travel'
        ? 'e.g. TRV-001 or Ticket ID'
        : 'Enter bill number'
    },
    receiptPolicyBadgeText() {
      const threshold = this.claimPolicies?.receiptThreshold || 500
      return this.isReceiptMandatory
        ? `Receipt Mandatory (> ₹${threshold})`
        : `Receipt Optional (≤ ₹${threshold})`
    },
    uploadReceiptText() {
      return this.isReceiptMandatory
        ? 'Upload receipt / invoice (Mandatory)'
        : 'Upload receipt / invoice (Optional)'
    }
  },
  watch: {
    'formState.month'() {
      this.fetchMonthlyDistance()
    }
  },
  created() {
    this.initMonthDefault()
    this.fetchClaimSettingsData()
    this.fetchUserClaims()
    this.fetchMonthlyDistance()
  },
  methods: {
    initMonthDefault() {
      // By default select September 2026 or current month if available
      const septOption = this.monthOptions.find((m) => m.value === 8)
      if (septOption) {
        this.formState.month = 8 // September
      } else {
        this.formState.month = dayjs().month()
      }
    },
    async fetchClaimSettingsData() {
      try {
        const params = {}
        if (this.userBranchId) {
          params.branchId = this.userBranchId
        }
        if (this.currentUserId) {
          params.userId = this.currentUserId
        }
        const res = await getClaimSettings(params)
        const data = res.data?.data || res.data || {}
        if (data.travelModes && Array.isArray(data.travelModes) && data.travelModes.length > 0) {
          const activeModes = data.travelModes.filter((m) => m.status !== 'INACTIVE' && m.isActive !== false)
          if (activeModes.length > 0) {
            this.travelModes = activeModes
          }
        }
        if (data.allowances && Array.isArray(data.allowances) && data.allowances.length > 0) {
          const activeAllowances = data.allowances.filter((a) => a.status !== 'INACTIVE' && a.isActive !== false)
          if (activeAllowances.length > 0) {
            this.allowances = activeAllowances
          }
        }
        if (data.claimPolicies && typeof data.claimPolicies === 'object') {
          this.claimPolicies = {
            ...this.claimPolicies,
            ...data.claimPolicies
          }
        }
        this.$nextTick(() => {
          this.selectClaimMode(0)
        })
      } catch (err) {
        console.warn('Could not fetch claim settings, using defaults:', err)
        this.$nextTick(() => {
          this.selectClaimMode(0)
        })
      }
    },
    selectClaimMode(idx) {
      this.selectedModeIndex = idx
      const mode = this.availableModes[idx]
      if (!mode) return

      if (mode.rateType === 'FLAT') {
        this.travelDistance = 0
        if (mode.categoryType === 'allowance') {
          this.formState.amount = mode.flatAmount || 0
        } else {
          // For fixed travel modes, amount starts at 0 unless editing
          if (!this.isEditingClaim) {
            this.formState.amount = 0
          }
        }
        if (!this.formState.billNumber) {
          this.formState.billNumber = `${mode.label.substring(0, 3).toUpperCase()}-${dayjs().format('MMDD')}`
        }
      } else if (mode.rateType === 'PER_KM') {
        if (!this.isEditingClaim) {
          this.travelDistance = 0
          this.formState.amount = 0
        } else if (!this.travelDistance) {
          this.travelDistance = 0
          this.formState.amount = 0
        }
        if (!this.formState.billNumber) {
          this.formState.billNumber = `TRV-${mode.label.substring(0, 3).toUpperCase()}-${dayjs().format('MMDD')}`
        }
      } else {
        this.travelDistance = 0
        if (!this.isEditingClaim) {
          this.formState.amount = 0
        }
      }

      this.$nextTick(() => {
        const btn = this.$refs[`modeBtn_${idx}`]?.[0] || this.$refs[`modeBtn_${idx}`]
        if (btn && btn.scrollIntoView) {
          btn.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' })
        }
      })
    },
    onDistanceInput() {
      if (!this.selectedMode || this.selectedMode.rateType !== 'PER_KM') return
      const dist = Math.max(0, Number(this.travelDistance) || 0)
      const rate = Number(this.selectedMode.pricePerKm) || 0
      let amt = dist * rate
      if (this.selectedMode.min && amt < Number(this.selectedMode.min)) {
        amt = Number(this.selectedMode.min)
      }
      if (this.selectedMode.max && amt > Number(this.selectedMode.max)) {
        amt = Number(this.selectedMode.max)
      }
      this.formState.amount = amt > 0 ? amt : null
    },
    resetToGpsDistance() {
      this.travelDistance = this.totalMonthlyDistance
      this.onDistanceInput()
    },
    setDayGpsDistance() {
      if (this.dailyDistances[this.formState.billDate]) {
        this.travelDistance = Number((this.dailyDistances[this.formState.billDate] || 0).toFixed(1))
        this.onDistanceInput()
      }
    },
    getModeShortLabel(modeKey) {
      const map = {
        TWO_WHEELER: 'Bike',
        FOUR_WHEELER: 'Car',
        AUTO: 'Auto',
        BUS: 'Bus',
        TRAIN: 'Train',
        FLIGHT: 'Flight',
        OTHER: 'Other'
      }
      return map[modeKey] || modeKey || 'Travel'
    },
    getAllowanceShortLabel(typeKey) {
      const map = {
        DAILY_DA: 'Daily DA',
        HALF_DAY_DA: 'Half DA',
        OUTSTATION_DA: 'Outstation',
        NIGHT_STAY_DA: 'Night Stay',
        MEAL_DA: 'Meal DA'
      }
      return map[typeKey] || typeKey || 'Allowance'
    },
    getAllowanceIcon(typeKey) {
      const map = {
        DAILY_DA: 'mdi-cash-fast',
        HALF_DAY_DA: 'mdi-clock-outline',
        OUTSTATION_DA: 'mdi-map-marker-path',
        NIGHT_STAY_DA: 'mdi-bed',
        MEAL_DA: 'mdi-silverware-fork-knife'
      }
      return map[typeKey] || 'mdi-cash-fast'
    },
    formatModeName(modeKey) {
      const map = {
        TWO_WHEELER: 'Bike / Scooter',
        FOUR_WHEELER: 'Car (Personal)',
        AUTO: 'Auto Rickshaw',
        BUS: 'Public Bus',
        TRAIN: 'Train / Metro',
        FLIGHT: 'Flight / Air',
        OTHER: 'Other Travel'
      }
      return map[modeKey] || modeKey || 'Travel'
    },
    getModeMeta(modeKey) {
      const meta = {
        TWO_WHEELER: { icon: 'mdi-motorbike', color: '#1677ff' },
        FOUR_WHEELER: { icon: 'mdi-car', color: '#059669' },
        AUTO: { icon: 'mdi-rickshaw', color: '#d97706' },
        BUS: { icon: 'mdi-bus', color: '#7c3aed' },
        TRAIN: { icon: 'mdi-train', color: '#dc2626' },
        FLIGHT: { icon: 'mdi-airplane', color: '#0284c7' },
        OTHER: { icon: 'mdi-map-marker-distance', color: '#475569' }
      }
      return meta[modeKey] || { icon: 'mdi-map-marker-distance', color: '#1677ff' }
    },
    formatAllowanceTypeName(typeKey) {
      const map = {
        DAILY_DA: 'Daily Allowance (Full Day)',
        HALF_DAY_DA: 'Half Day Allowance',
        OUTSTATION_DA: 'Outstation Allowance',
        NIGHT_STAY_DA: 'Night Stay / Hotel DA',
        MEAL_DA: 'Meal / Refreshment DA'
      }
      return map[typeKey] || typeKey || 'Daily Allowance'
    },
    getTargetCategoryId(mode) {
      if (!mode) return null
      return mode.key || mode.mode || mode.id
    },
    handleSliderMouseDown(e) {
      if (!this.$refs.sliderTrack) return
      this.isDraggingSlider = true
      this.sliderStartX = e.pageX - this.$refs.sliderTrack.offsetLeft
      this.sliderScrollLeft = this.$refs.sliderTrack.scrollLeft
    },
    handleSliderMouseLeave() {
      this.isDraggingSlider = false
    },
    handleSliderMouseUp() {
      this.isDraggingSlider = false
    },
    handleSliderMouseMove(e) {
      if (!this.isDraggingSlider || !this.$refs.sliderTrack) return
      e.preventDefault()
      const x = e.pageX - this.$refs.sliderTrack.offsetLeft
      const walk = (x - this.sliderStartX) * 1.5
      this.$refs.sliderTrack.scrollLeft = this.sliderScrollLeft - walk
    },
    formatNumber(num) {
      const val = Number(num) || 0
      return new Intl.NumberFormat('en-IN').format(val)
    },
    formatDateTime(dateStr) {
      if (!dateStr) return ''
      return dayjs(dateStr).format('DD MMM, YYYY')
    },
    formatFileSize(bytes) {
      if (!bytes) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
    },
    triggerFileInput() {
      if (this.$refs.fileInputRef) {
        this.$refs.fileInputRef.click()
      }
    },
    handleFileChange(e) {
      const file = e.target.files?.[0]
      if (!file) return

      // Validate size (max 5MB)
      const maxSize = 5 * 1024 * 1024
      if (file.size > maxSize) {
        this.$toast?.error('File size exceeds the 5MB limit. Please choose a smaller file.')
        return
      }

      this.selectedFile = file

      // Generate preview if image
      if (file.type.startsWith('image/')) {
        this.filePreviewUrl = URL.createObjectURL(file)
      } else {
        this.filePreviewUrl = null
      }
    },
    removeSelectedFile() {
      this.selectedFile = null
      if (this.filePreviewUrl) {
        URL.revokeObjectURL(this.filePreviewUrl)
        this.filePreviewUrl = null
      }
      if (this.$refs.fileInputRef) {
        this.$refs.fileInputRef.value = ''
      }
    },

    async fetchUserClaims() {
      if (!this.currentUserId) return
      this.isLoadingClaims = true
      try {
        const res = await getUserReimbursements(this.currentUserId, {
          year: dayjs().year()
        })
        this.userClaims = res.data?.reimbursements || []
      } catch (err) {
        console.warn('Failed to load user claims:', err)
      } finally {
        this.isLoadingClaims = false
      }
    },
    getClaimStatus(claim) {
      if (!claim) return 'PENDING'
      if (claim.approvals && claim.approvals.length > 0) {
        const last = claim.approvals[claim.approvals.length - 1]
        return (last.status || 'Pending').toUpperCase()
      }
      return (claim.status || 'Pending').toUpperCase()
    },
    getStatusBadgeClass(status) {
      switch (status) {
        case 'APPROVED':
          return 'bg-emerald-50 text-emerald-700 border-emerald-200'
        case 'REJECTED':
          return 'bg-rose-50 text-rose-700 border-rose-200'
        case 'RESUBMITTED':
          return 'bg-sky-50 text-sky-700 border-sky-200'
        case 'PENDING':
        default:
          return 'bg-amber-50 text-amber-700 border-amber-200'
      }
    },
    getCategoryLabel(typeId) {
      if (!typeId) return 'Expense Claim'
      const modeMatch = this.availableModes.find(
        (m) => m.id === typeId || m.key === typeId || m.mode === typeId
      )
      if (modeMatch) return modeMatch.fullName || modeMatch.label
      return typeId
    },
    getAttachmentFullUrl(path) {
      if (!path) return '#'
      if (path.startsWith('http://') || path.startsWith('https://')) return path
      return `${this.s3BaseUrl}${path}`
    },
    canEditClaim(claim) {
      const status = this.getClaimStatus(claim)
      return status === 'PENDING'
    },
    async fetchMonthlyDistance() {
      if (!this.currentUserId) return
      try {
        const selectedMonthObj = this.monthOptions.find((m) => m.value === this.formState.month)
        const year = selectedMonthObj?.year || dayjs().year()
        const monthIdx = this.formState.month !== undefined ? this.formState.month : dayjs().month()
        const monthDate = dayjs().year(year).month(monthIdx)
        const start = monthDate.startOf('month').format('YYYY-MM-DD')
        const end = monthDate.endOf('month').format('YYYY-MM-DD')

        const res = await getLocationOverview({
          userId: this.currentUserId,
          start,
          end
        })

        const locs = res.data?.locationHistory || res.data?.data || res.data || []
        let totalDist = 0
        const distMap = {}
        if (Array.isArray(locs)) {
          locs.forEach((item) => {
            const d = dayjs(item.travelDate || item.date || item.createdAt).format('YYYY-MM-DD')
            const dist = item.dailyDistanceTraveled?.distance || 0
            distMap[d] = dist
            totalDist += dist
          })
        }
        this.dailyDistances = distMap
        this.totalMonthlyDistance = Number(totalDist.toFixed(1))
      } catch (err) {
        console.warn('Could not fetch monthly location distance:', err)
      }
    },
    openEditClaimInForm(claim) {
      this.isEditingClaim = true
      this.editingClaim = claim

      // 1. Month match
      if (claim.month) {
        const foundMonth = this.monthOptions.find(
          (m) => m.monthName.toLowerCase() === claim.month.toLowerCase()
        )
        if (foundMonth) {
          this.formState.month = foundMonth.value
        }
      }

      // 2. Form values
      this.formState.billNumber = claim.billNumber || ''
      this.formState.billDate = claim.billDate ? dayjs(claim.billDate).format('YYYY-MM-DD') : ''
      this.formState.amount = claim.claimAmount || null
      const lastComment = claim.comments?.[claim.comments.length - 1]
      this.formState.comment = lastComment?.message || ''

      // 3. Match Mode in availableModes
      const modeKey = claim.travelDetails?.mode || claim.reimbursementType
      const foundIdx = this.availableModes.findIndex(
        (m) =>
          m.key === modeKey ||
          m.mode === modeKey ||
          m.id === modeKey ||
          (claim.reimbursementTypeName &&
            m.fullName &&
            m.fullName.toLowerCase() === claim.reimbursementTypeName.toLowerCase()) ||
          (claim.reimbursementTypeName &&
            m.label &&
            m.label.toLowerCase() === claim.reimbursementTypeName.toLowerCase())
      )
      if (foundIdx !== -1) {
        this.selectedModeIndex = foundIdx
      }

      // 4. Distance
      if (claim.travelDetails?.distanceKm) {
        this.travelDistance = Number(claim.travelDetails.distanceKm)
      } else {
        this.travelDistance = this.totalMonthlyDistance
      }

      // 5. Attachment
      this.removeSelectedFile()
      this.attachmentUrlInput = ''
      this.showUrlAttachmentInput = false
      this.existingAttachedBill = claim.attachedBill || null

      // 6. Switch to Form view
      this.activeView = 'form'
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    cancelEditClaim() {
      this.isEditingClaim = false
      this.editingClaim = null
      this.existingAttachedBill = null
      this.attachmentUrlInput = ''
      this.showUrlAttachmentInput = false
      this.removeSelectedFile()
      this.formState.billNumber = ''
      this.formState.billDate = dayjs().format('YYYY-MM-DD')
      this.formState.amount = null
      this.formState.comment = ''
      if (this.selectedMode && this.selectedMode.rateType === 'PER_KM') {
        this.travelDistance = this.totalMonthlyDistance
        this.onDistanceInput()
      } else {
        this.travelDistance = 0
      }
    },
    removeExistingAttachment() {
      this.existingAttachedBill = null
    },
    async handleSubmitClaim() {
      // 1. Validation: Bill Number (Auto-fill for travel/allowance if omitted)
      if (!this.formState.billNumber) {
        if (this.selectedMode?.categoryType === 'travel') {
          this.formState.billNumber = `TRV-${dayjs().format('MMDD-HHmmss')}`
        } else if (this.selectedMode?.categoryType === 'allowance') {
          this.formState.billNumber = `DA-${dayjs().format('MMDD-HHmmss')}`
        } else {
          this.$toast?.error('Please enter the bill number.')
          return
        }
      }

      // 2. Validation: Expense Date & Policy Submission Window
      if (!this.formState.billDate) {
        this.$toast?.error('Please select the expense date.')
        return
      }

      const claimDate = dayjs(this.formState.billDate)
      const today = dayjs()
      if (claimDate.isAfter(today, 'day')) {
        this.$toast?.error('Expense date cannot be in the future.')
        return
      }
      if (this.claimPolicies?.allowPastDateClaims === false && !claimDate.isSame(today, 'day')) {
        this.$toast?.error('Past date claims are not allowed per company policy.')
        return
      }
      const windowDays = Number(this.claimPolicies?.submissionWindowDays) || 30
      if (today.diff(claimDate, 'day') > windowDays) {
        this.$toast?.error(`Claims older than ${windowDays} days cannot be submitted per company policy.`)
        return
      }

      // 3. Validation: Amount, Mode Maximum Limit & Daily Cap
      const amount = Number(this.formState.amount)
      if (!amount || amount <= 0) {
        this.$toast?.error('Please enter a valid claim amount.')
        return
      }
      if (this.selectedMode && this.selectedMode.rateType === 'FLAT' && this.selectedMode.categoryType === 'travel') {
        const modeMax = this.selectedModeMaxLimit
        if (modeMax > 0 && amount > modeMax) {
          this.$toast?.error(`Claim amount exceeds the maximum limit of ₹${this.formatNumber(modeMax)} allowed for ${this.selectedMode.fullName}.`)
          return
        }
      }
      if (this.claimPolicies?.maxDailyClaimCap && amount > Number(this.claimPolicies.maxDailyClaimCap)) {
        this.$toast?.error(`Claim amount exceeds the company daily cap of ₹${this.formatNumber(this.claimPolicies.maxDailyClaimCap)}.`)
        return
      }

      // 4. Validation: Receipt Mandatory Check
      const hasAttachment = !!(
        this.selectedFile ||
        this.existingAttachedBill ||
        (this.attachmentUrlInput && this.attachmentUrlInput.trim())
      )
      if (this.isReceiptMandatory && !hasAttachment) {
        const threshold = this.claimPolicies?.receiptThreshold || 500
        this.$toast?.error(`A receipt attachment is mandatory for claims above ₹${threshold} or for the selected mode.`)
        return
      }

      this.isSubmitting = true
      try {
        const selectedMonthObj = this.monthOptions.find((m) => m.value === this.formState.month)
        const monthName = selectedMonthObj?.monthName || 'September'
        const year = selectedMonthObj?.year || dayjs().year()

        // 5. Upload Document First (if new file attached)
        let attachedDocPath = null
        if (this.selectedFile) {
          const uploadFormData = new FormData()
          uploadFormData.append('attachments', this.selectedFile)
          uploadFormData.append('month', monthName.toLowerCase())
          if (this.currentUserId) {
            uploadFormData.append('userId', this.currentUserId)
          }

          const uploadRes = await uploadReimbursementDocument(uploadFormData)
          if (uploadRes.data?.success && uploadRes.data?.url?.doc_physical_path) {
            attachedDocPath = uploadRes.data.url.doc_physical_path
          } else if (uploadRes.data?.url) {
            attachedDocPath = typeof uploadRes.data.url === 'string' ? uploadRes.data.url : uploadRes.data.url.doc_physical_path
          } else {
            throw new Error('Failed to upload receipt attachment.')
          }
        } else if (this.attachmentUrlInput && this.attachmentUrlInput.trim()) {
          attachedDocPath = this.attachmentUrlInput.trim()
        } else if (this.existingAttachedBill) {
          attachedDocPath = this.existingAttachedBill
        }

        // 6. Travel Details Metadata
        const isPerKm = this.selectedMode?.rateType === 'PER_KM'
        const sysDist = isPerKm ? Number(this.totalMonthlyDistance) || 0 : 0
        const sysAmt = isPerKm ? Math.round(sysDist * (Number(this.selectedMode.pricePerKm) || 0)) : 0
        const claimDist = isPerKm ? Number(this.travelDistance) || 0 : 0

        const travelDetails = this.selectedMode?.categoryType === 'travel' ? {
          mode: this.selectedMode.mode,
          rateType: this.selectedMode.rateType,
          pricePerKm: this.selectedMode.pricePerKm,
          distanceKm: isPerKm ? claimDist : null,
          systemDistanceKm: sysDist,
          systemCalculatedAmount: sysAmt,
          claimDistanceKm: claimDist
        } : null

        // 7. Auto-generate Comment if empty
        let commentMsg = this.formState.comment ? this.formState.comment.trim() : ''
        if (!commentMsg) {
          if (this.selectedMode?.categoryType === 'travel') {
            const distText = this.selectedMode.rateType === 'PER_KM' && this.travelDistance ? ` (${this.travelDistance} km)` : ''
            commentMsg = `Travel via ${this.selectedMode.fullName}${distText}`
          } else if (this.selectedMode?.categoryType === 'allowance') {
            commentMsg = `${this.selectedMode.fullName} (₹${this.selectedMode.flatAmount})`
          } else {
            commentMsg = `${this.selectedMode?.label || 'Expense'} claim submitted via employee portal`
          }
        }

        const targetReimbursementTypeId = this.getTargetCategoryId(this.selectedMode)

        // 8. Submit or Update via API
        if (this.isEditingClaim && this.editingClaim) {
          const updatePayload = {
            user: this.currentUserId,
            reimbursementType: targetReimbursementTypeId,
            reimbursementTypeName: this.selectedMode?.fullName || this.selectedMode?.label || 'Expense Claim',
            claimAmount: Number(this.formState.amount),
            month: monthName,
            year: year,
            billNumber: this.formState.billNumber,
            billDate: this.formState.billDate,
            attachedBill: attachedDocPath,
            travelDetails,
            approvals: [
              ...(this.editingClaim.approvals || []),
              { status: 'Pending' }
            ],
            comments: [
              ...(this.editingClaim.comments || []),
              {
                message: commentMsg || 'Claim updated',
                commentedBy: this.currentUserId,
                commentedAt: new Date().toISOString()
              }
            ]
          }
          const claimId = this.editingClaim._id || this.editingClaim.id
          await updateReimbursement(claimId, updatePayload)
          this.$toast?.success('Your claim has been updated successfully!')
          this.cancelEditClaim()
        } else {
          const payload = {
            user: this.currentUserId,
            reimbursementType: targetReimbursementTypeId,
            reimbursementTypeName: this.selectedMode?.fullName || this.selectedMode?.label || 'Expense Claim',
            claimAmount: Number(this.formState.amount),
            month: monthName,
            year: year,
            billNumber: this.formState.billNumber,
            billDate: this.formState.billDate,
            attachedBill: attachedDocPath,
            travelDetails,
            comments: [
              {
                message: commentMsg,
                commentedBy: this.currentUserId,
                commentedAt: new Date().toISOString()
              }
            ],
            approvals: [
              {
                status: 'Pending'
              }
            ]
          }
          await submitReimbursement(payload)
          this.$toast?.success('Your claim has been submitted successfully!')
          this.cancelEditClaim()
        }

        // 9. Refresh claims data and switch to history
        await this.fetchUserClaims()
        this.activeView = 'history'
      } catch (err) {
        console.error('Claim submission error:', err)
        const errorMsg = err.response?.data?.message || err.message || 'Failed to submit claim.'
        this.$toast?.error(errorMsg)
      } finally {
        this.isSubmitting = false
      }
    },
    openDiscussionModal(claim) {
      this.selectedDiscussionClaim = claim
      this.discussionReplyText = ''
      this.discussionModalOpen = true
    },
    closeDiscussionModal() {
      this.discussionModalOpen = false
      this.selectedDiscussionClaim = null
      this.discussionReplyText = ''
    },
    formatFullDateTime(dateStr) {
      if (!dateStr) return ''
      return dayjs(dateStr).format('DD MMM YYYY, hh:mm A')
    },
    isCommentByEmployee(c) {
      if (c.commentedByRole === 'EMPLOYEE') return true
      if (c.commentedByRole === 'ADMIN') return false
      return String(c.commentedBy) === String(this.currentUserId)
    },
    getTimelineDotClass(status) {
      const s = (status || '').toUpperCase()
      if (s === 'APPROVED') return 'bg-emerald-500'
      if (s === 'REJECTED') return 'bg-red-500'
      if (s === 'RESUBMITTED') return 'bg-amber-500'
      if (s === 'REVIEW') return 'bg-purple-500'
      return 'bg-blue-500'
    },
    async sendDiscussionReply() {
      if (!this.discussionReplyText || !this.discussionReplyText.trim() || !this.selectedDiscussionClaim) return
      const claimId = this.selectedDiscussionClaim._id || this.selectedDiscussionClaim.id
      if (!claimId) return

      this.isSendingDiscussionReply = true
      const text = this.discussionReplyText.trim()
      try {
        await addClaimComment(claimId, text)
        const user = this.currentUser || {}
        const newCommentObj = {
          message: text,
          commentedBy: this.currentUserId,
          commentedByName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Employee',
          commentedByRole: 'EMPLOYEE',
          commentedAt: new Date().toISOString()
        }
        if (!Array.isArray(this.selectedDiscussionClaim.comments)) {
          this.$set(this.selectedDiscussionClaim, 'comments', [])
        }
        this.selectedDiscussionClaim.comments.push(newCommentObj)
        this.discussionReplyText = ''
        this.$toast?.success('Message sent!')
      } catch (err) {
        console.error('Failed to send comment:', err)
        this.$toast?.error(err.response?.data?.message || err.message || 'Failed to send message.')
      } finally {
        this.isSendingDiscussionReply = false
      }
    }
  }
}
</script>

<style scoped>
/* Minimal Mode Carousel Custom Styling */
.mode-carousel-wrapper {
  width: 100%;
}

.mode-carousel-track {
  width: 100%;
  display: flex;
  align-items: center;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-x;
  overscroll-behavior-x: contain;
  cursor: grab;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.mode-carousel-track:active {
  cursor: grabbing;
}

.mode-carousel-track::-webkit-scrollbar {
  display: none;
}

.mode-carousel-btn {
  outline: none !important;
  user-select: none !important;
  -webkit-user-select: none !important;
  -webkit-tap-highlight-color: transparent !important;
}

.mode-carousel-btn:focus {
  outline: none !important;
}

/* Vuetify Field Overrides to match precise rounded, clean aesthetic */
.custom-vuetify-input.v-text-field--outlined>>>fieldset {
  border-radius: 12px !important;
  border-color: #cbd5e1 !important;
  transition: border-color 0.2s ease;
}

.custom-vuetify-input.v-text-field--outlined.v-input--is-focused>>>fieldset {
  border-color: #1677ff !important;
  border-width: 1.5px !important;
}

.custom-vuetify-input>>>.v-input__control {
  min-height: 42px !important;
}

.custom-vuetify-input>>>.v-input__slot {
  min-height: 42px !important;
  padding: 0 12px !important;
  background-color: white !important;
}

.custom-vuetify-input>>>input,
.custom-vuetify-input>>>textarea {
  color: #1e293b !important;
  font-size: 14px !important;
}

.custom-vuetify-input>>>.v-text-field__prefix {
  font-weight: 600 !important;
  color: #334155 !important;
  margin-top: 0 !important;
}

/* Hide scrollbars for chips */
.no-scrollbar::-webkit-scrollbar {
  display: none;
}

.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
